/**
 * Generate Preview HTML for iframe-based page builder preview
 *
 * This module generates a complete HTML document string that is injected
 * into an iframe for true viewport isolation in the page builder preview.
 *
 * Component rendering is delegated to the unified SectionRenderer (mode="editor").
 * This file handles: document shell, editor CSS/JS, formatting toolbar,
 * font collection, native sections, and CSS aggregation.
 */

import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  getResponsiveDimension,
  resolveSpacing,
} from '../../renderer/StyleResolver';
import { colorValueToCss, colorValueToSolidCss } from '../../fields/ColorPicker/colorUtils';
import { hasLinkUrl, styleObjectToCSS } from '../../renderer/renderUtils';
import SectionRenderer from '../../renderer/SectionRenderer';

// ============================================================================
// Module-level mode state (set per-call in generatePreviewHTML)
// ============================================================================

let _currentMode = 'editor'; // 'editor' | 'publish'
let _publishConfig = {};     // { title, metaDescription, faviconUrl, siteSections, pageIdToSlugMap }


/**
 * Returns editor section data attributes, or empty string in publish mode.
 */
function editorSectionAttrs(sectionId, extras = {}) {
  if (_currentMode !== 'editor') return '';
  let attrs = `data-section-id="${sectionId}"`;
  for (const [key, value] of Object.entries(extras)) {
    attrs += ` ${key}="${value}"`;
  }
  return attrs;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Resolve a link value to a URL string.
 * Local version with publish-mode pageIdToSlugMap support.
 * @param {string|Object} linkValue - URL string or { type: "internal", pageId: string }
 * @returns {string} Resolved URL string
 */
function resolveLinkUrl(linkValue) {
  if (!linkValue) return '';
  if (typeof linkValue === 'string') return linkValue;
  if (typeof linkValue === 'object' && linkValue.type === 'internal' && linkValue.pageId) {
    // In publish mode, map page IDs to slug-based URLs
    if (_currentMode === 'publish' && _publishConfig.pageIdToSlugMap) {
      const slug = _publishConfig.pageIdToSlugMap[linkValue.pageId];
      if (slug) return `/${slug}`;
    }
    return `/pages/${linkValue.pageId}`;
  }
  return '';
}

/**
 * Get font family CSS value (local version for native sections)
 */
function getFontFamilyCSS(fontFamily) {
  if (!fontFamily || fontFamily === 'System Default') {
    return "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
  }
  return `'${fontFamily}', sans-serif`;
}

/**
 * Generate Google Fonts link tags for used fonts
 */
function generateGoogleFontsLinks(fonts) {
  if (!fonts || fonts.size === 0) return '';

  const systemFonts = ['System Default', 'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Trebuchet MS', 'Comic Sans MS', 'Impact'];
  const googleFonts = Array.from(fonts).filter(font => !systemFonts.includes(font));
  if (googleFonts.length === 0) return '';

  const families = googleFonts
    .map(font => `family=${font.replace(/\s+/g, '+')}:wght@400;500;600;700`)
    .join('&');

  return `<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?${families}&display=swap" rel="stylesheet">`;
}

/**
 * Escape HTML entities
 */
function escapeHTML(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// ============================================================================
// Font Collection
// ============================================================================

/**
 * Collect all fonts used in sections
 */
export function collectUsedFonts(sections) {
  const fonts = new Set();

  function addFont(fontFamily) {
    if (fontFamily && typeof fontFamily === 'string' && fontFamily !== 'System Default') {
      fonts.add(fontFamily);
    }
  }

  function collectFromComponent(component) {
    if (!component) return;
    const { props } = component;

    if (props?.fontFamily) addFont(props.fontFamily);

    if (props?.content && Array.isArray(props.content)) {
      props.content.forEach(item => {
        if (item && typeof item === 'object') {
          if (item.fontFamily) addFont(item.fontFamily);
          if (item.styles?.fontFamily) addFont(item.styles.fontFamily);
        }
      });
    }

    if (props?.text && Array.isArray(props.text)) {
      props.text.forEach(item => {
        if (item && typeof item === 'object' && item.fontFamily) addFont(item.fontFamily);
      });
    }

    if (component.children && Array.isArray(component.children)) {
      component.children.forEach(collectFromComponent);
    }
  }

  sections.forEach(section => {
    if (section.section_type === 'layout-1' && section.content?.components) {
      section.content.components.forEach(collectFromComponent);
    }
    if (section.content?.fontFamily) addFont(section.content.fontFamily);
  });

  return fonts;
}

// ============================================================================
// CSS Collection
// ============================================================================

// ============================================================================
// Layout Section HTML (via unified SectionRenderer)
// ============================================================================

/**
 * Generate HTML for a layout section using the unified SectionRenderer
 */
export function generateLayoutSectionHTML(content, sectionId) {
  return renderToStaticMarkup(
    <SectionRenderer content={content} sectionId={sectionId} mode={_currentMode} />
  );
}

// ============================================================================
// Native Section HTML Generation
// ============================================================================

/**
 * Generate HTML for native sections (header, paragraph, image, video, cta)
 */
export function generateNativeSectionHTML(section) {
  const { section_type, content = {}, id } = section;
  const dataAttr = editorSectionAttrs(id);

  // Build section wrapper styles (spacing, sizing, border)
  const wrapperStyle = {};

  // Spacing - support both responsive format and legacy individual fields
  if (content.padding && content.padding.base !== undefined) {
    Object.assign(wrapperStyle, resolveSpacing(content.padding, 'padding', 'base'));
  } else {
    wrapperStyle.padding = '16px';
  }
  if (content.margin && content.margin.base !== undefined) {
    Object.assign(wrapperStyle, resolveSpacing(content.margin, 'margin', 'base'));
  } else {
    wrapperStyle.margin = '0';
  }

  // Sizing
  if (content.sectionWidth || content.width) {
    const key = content.sectionWidth ? 'sectionWidth' : 'width';
    const w = getResponsiveDimension(content[key], 'base');
    if (w !== 'auto') wrapperStyle.width = w;
  }
  if (content.sectionHeight || content.height) {
    if (content.sectionHeight) {
      const h = getResponsiveDimension(content.sectionHeight, 'base');
      if (h !== 'auto') wrapperStyle.height = h;
    }
  }
  if (content.minWidth) {
    const v = getResponsiveDimension(content.minWidth, 'base');
    if (v !== 'auto') wrapperStyle.minWidth = v;
  }
  if (content.minHeight) {
    const v = getResponsiveDimension(content.minHeight, 'base');
    if (v !== 'auto') wrapperStyle.minHeight = v;
  }
  if (content.maxWidth) {
    const v = getResponsiveDimension(content.maxWidth, 'base');
    if (v !== 'auto') wrapperStyle.maxWidth = v;
  }
  if (content.maxHeight) {
    const v = getResponsiveDimension(content.maxHeight, 'base');
    if (v !== 'auto') wrapperStyle.maxHeight = v;
  }

  // Border
  if (content.sectionBorderWidth && content.sectionBorderWidth > 0) {
    wrapperStyle.borderWidth = `${content.sectionBorderWidth}px`;
    wrapperStyle.borderStyle = content.sectionBorderStyle || 'solid';
    wrapperStyle.borderColor = colorValueToSolidCss(content.sectionBorderColor, 'transparent');
  }
  if (content.sectionBorderRadius && content.sectionBorderRadius > 0) {
    wrapperStyle.borderRadius = `${content.sectionBorderRadius}px`;
  }

  // Background (for native sections that use sectionBackgroundColor on the wrapper)
  if (section_type !== 'image' && section_type !== 'video') {
    const bg = colorValueToCss(content.sectionBackgroundColor, 'transparent');
    if (bg !== 'transparent') wrapperStyle.background = bg;
  }

  // Overflow
  if (content.overflow && content.overflow !== 'visible') {
    wrapperStyle.overflow = content.overflow;
  }

  switch (section_type) {
    case 'header': {
      const style = {
        fontFamily: content.fontFamily ? getFontFamilyCSS(content.fontFamily) : undefined,
        fontSize: content.fontSize ? getResponsiveDimension(content.fontSize) : '32px',
        fontWeight: content.fontWeight || 'bold',
        color: colorValueToSolidCss(content.color, '#1f2937'),
        textAlign: content.textAlign || 'left',
      };
      const text = escapeHTML(content.text || 'Header');
      const element = content.element || 'h1';
      return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS(wrapperStyle)}"><${element} style="${styleObjectToCSS(style)}">${text}</${element}></div>`;
    }

    case 'paragraph': {
      const style = {
        fontFamily: content.fontFamily ? getFontFamilyCSS(content.fontFamily) : undefined,
        fontSize: content.fontSize ? getResponsiveDimension(content.fontSize) : '16px',
        fontWeight: content.fontWeight || 'normal',
        color: colorValueToSolidCss(content.color, '#1f2937'),
        textAlign: content.textAlign || 'left',
        lineHeight: content.lineHeight || '1.6',
      };
      const text = escapeHTML(content.text || 'Paragraph text');
      return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS(wrapperStyle)}"><p style="${styleObjectToCSS(style)}">${text}</p></div>`;
    }

    case 'image': {
      if (!content.src) {
        return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS({ ...wrapperStyle, textAlign: 'center', color: '#9ca3af', background: '#f3f4f6' })}">No image source</div>`;
      }
      const imgBorderRadius = content.imageBorderRadius || content.borderRadius || 0;
      const style = {
        display: 'block',
        maxWidth: '100%',
        width: content.width ? `${content.width}px` : 'auto',
        height: content.height && content.height !== 'auto' ? `${content.height}px` : 'auto',
        objectFit: content.objectFit || 'cover',
        borderRadius: `${imgBorderRadius}px`,
        margin: content.textAlign === 'center' ? '0 auto' : undefined,
        opacity: content.opacity !== undefined && content.opacity !== 100 ? content.opacity / 100 : undefined,
        filter: content.filter && content.filter !== 'none' ? content.filter : undefined,
      };
      wrapperStyle.textAlign = content.textAlign || 'center';
      return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS(wrapperStyle)}"><img src="${content.src}" alt="${content.alt || ''}" style="${styleObjectToCSS(style)}" /></div>`;
    }

    case 'video': {
      if (!content.url && !content.src) {
        return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS({ ...wrapperStyle, textAlign: 'center', color: '#9ca3af', background: '#f3f4f6' })}">No video source</div>`;
      }
      const videoSrc = content.url || content.src;
      const style = {
        display: 'block',
        maxWidth: '100%',
        width: content.width ? `${content.width}px` : '100%',
        borderRadius: `${content.borderRadius || 0}px`,
        background: '#000',
      };
      const videoAttrs = [
        content.controls !== false ? 'controls' : '',
        content.autoplay ? 'autoplay' : '',
        content.loop ? 'loop' : '',
        content.muted ? 'muted' : '',
        'playsinline',
      ].filter(Boolean).join(' ');
      wrapperStyle.textAlign = content.textAlign || 'center';
      return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS(wrapperStyle)}"><video ${videoAttrs} style="${styleObjectToCSS(style)}">
        <source src="${videoSrc}" type="${content.type || 'video/mp4'}" />
      </video></div>`;
    }

    case 'cta': {
      const buttonStyle = {
        display: 'inline-block',
        padding: '12px 24px',
        fontFamily: content.fontFamily ? getFontFamilyCSS(content.fontFamily) : undefined,
        fontSize: content.fontSize ? getResponsiveDimension(content.fontSize) : '16px',
        fontWeight: content.fontWeight || '500',
        color: colorValueToSolidCss(content.textColor, '#ffffff'),
        background: colorValueToCss(content.backgroundColor, '#4f46e5'),
        border: 'none',
        borderRadius: `${content.borderRadius || 8}px`,
        cursor: 'pointer',
        textDecoration: 'none',
      };
      wrapperStyle.textAlign = content.alignment || content.textAlign || 'center';
      const text = escapeHTML(content.text || 'Call to Action');
      const button = hasLinkUrl(content.url)
        ? `<a href="${resolveLinkUrl(content.url)}" target="${content.openInNewTab ? '_blank' : '_self'}" rel="${content.openInNewTab ? 'noopener noreferrer' : ''}" style="${styleObjectToCSS(buttonStyle)}">${text}</a>`
        : `<button style="${styleObjectToCSS(buttonStyle)}">${text}</button>`;
      return `<div id="${id}" ${dataAttr} style="${styleObjectToCSS(wrapperStyle)}">${button}</div>`;
    }

    default:
      return `<div id="${id}" ${dataAttr} style="padding: 16px; color: #9ca3af;">Unknown section type: ${section_type}</div>`;
  }
}

// ============================================================================
// Main Export Function
// ============================================================================

/**
 * Generate a complete HTML document for the preview iframe or published site
 * @param {Array} sections - Page sections array
 * @param {Object} pageConfig - Page configuration (background color, etc.)
 * @param {string} mode - 'editor' (default) or 'publish'
 * @param {Object} publishConfig - Config for publish mode { title, metaDescription, faviconUrl, siteSections, pageIdToSlugMap }
 * @returns {string} Complete HTML document string
 */
export function generatePreviewHTML(sections, pageConfig = {}, mode = 'editor', publishConfig = {}) {
  // Set module-level mode state
  _currentMode = mode;
  _publishConfig = publishConfig;

  // In publish mode, prepend/append site sections (nav/footer)
  let allSections = sections;
  if (mode === 'publish' && publishConfig.siteSections) {
    const navSections = publishConfig.siteSections.filter(s => s.name?.toLowerCase().includes('nav') || s.order_index < 0);
    const footerSections = publishConfig.siteSections.filter(s => s.name?.toLowerCase().includes('footer') || s.order_index > 1000);
    allSections = [
      ...navSections.map(s => ({ id: s.id, section_type: 'layout-1', content: s.content })),
      ...sections,
      ...footerSections.map(s => ({ id: s.id, section_type: 'layout-1', content: s.content })),
    ];
  }

  // Collect fonts (CSS is handled inline by SectionRenderer in editor mode)
  const usedFonts = collectUsedFonts(allSections);
  const fontsLinks = generateGoogleFontsLinks(usedFonts);

  // Generate section HTML
  const sectionsHTML = allSections
    .map(section => {
      if (section.section_type === 'layout-1') {
        return generateLayoutSectionHTML(section.content || {}, section.id);
      }
      return generateNativeSectionHTML(section);
    })
    .join('\n');

  // Publish-only head elements
  const publishHead = mode === 'publish' ? `
  <title>${publishConfig.title ? escapeHTML(publishConfig.title) : ''}</title>
  ${publishConfig.metaDescription ? `<meta name="description" content="${escapeHTML(publishConfig.metaDescription)}">` : ''}
  ${publishConfig.faviconUrl ? `<link rel="icon" href="${publishConfig.faviconUrl}">` : ''}` : '';

  // Build the complete HTML document
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">${publishHead}
  ${fontsLinks}
  <style>
    /* Reset CSS */
    *, *::before, *::after {
      box-sizing: border-box;
    }

    html, body {
      margin: 0;
      padding: 0;
      overflow: ${mode === 'editor' ? 'hidden' : 'auto'};
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    img, video {
      max-width: 100%;
      height: auto;
    }

    button {
      font-family: inherit;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    ${mode === 'editor' ? `/* Selection highlight styles */
    [data-section-id].selected,
    [data-element-id].selected {
      outline: 1px dashed #3b82f6 !important;
      outline-offset: -2px;
      position: relative;
    }

    [data-section-id]:hover:not(.selected),
    [data-element-id]:hover:not(.selected) {
      outline: 2px solid #93c5fd !important;
      outline-offset: -2px;
      cursor: pointer;
    }

    /* Hover class for programmatic hover */
    [data-element-id].hover:not(.selected) {
      outline: 2px dashed #93c5fd !important;
      outline-offset: -2px;
    }

    /* Hover type label */
    #hover-type-label {
      position: fixed;
      z-index: 10001;
      pointer-events: none;
      display: none;
      padding: 2px 8px;
      background-color: #93c5fd;
      color: #1e3a5f;
      font-size: 11px;
      font-weight: 600;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      border-radius: 3px;
      white-space: nowrap;
      line-height: 16px;
    }

    /* Accordion styles */
    details summary::-webkit-details-marker { display: none; }
    details summary { list-style: none; }
    details .accordion-icon { transition: transform 0.2s ease; }
    details[open] .accordion-icon { transform: rotate(180deg); }

    /* Inline editing styles */
    [data-editable].editing {
      outline: 2px solid #3b82f6 !important;
      outline-offset: 0px;
      cursor: text;
      min-height: 1em;
    }

    [data-editable].editing:focus {
      outline: 2px solid #2563eb !important;
    }

    /* Formatting toolbar active state */
    #formatting-toolbar button.active {
      background: #374151 !important;
      color: #fff !important;
    }
    #formatting-toolbar button:hover {
      background: #374151;
    }

    /* Placement mode styles */
    body.placement-mode,
    body.placement-mode * {
      cursor: crosshair !important;
    }

    body.placement-mode [data-section-id]:hover:not(.selected),
    body.placement-mode [data-element-id]:hover:not(.selected) {
      outline: none !important;
    }

    body.placement-mode [data-element-id].hover:not(.selected) {
      outline: none !important;
    }

    body.placement-mode [data-element-id],
    body.placement-mode [data-section-id] > [data-element-id] {
      transition: transform 150ms ease;
    }

    .placement-indicator {
      position: absolute;
      pointer-events: none;
      z-index: 10000;
      transition: top 80ms ease, left 80ms ease, width 80ms ease, height 80ms ease;
    }

    .placement-indicator.horizontal {
      height: 3px;
      background: #3b82f6;
      border-radius: 2px;
    }

    .placement-indicator.horizontal::before,
    .placement-indicator.horizontal::after {
      content: '';
      position: absolute;
      width: 8px;
      height: 8px;
      background: #3b82f6;
      border-radius: 50%;
      top: 50%;
      transform: translateY(-50%);
    }

    .placement-indicator.horizontal::before { left: -4px; }
    .placement-indicator.horizontal::after { right: -4px; }

    .placement-indicator.vertical {
      width: 3px;
      background: #3b82f6;
      border-radius: 2px;
    }

    .placement-indicator.vertical::before,
    .placement-indicator.vertical::after {
      content: '';
      position: absolute;
      width: 8px;
      height: 8px;
      background: #3b82f6;
      border-radius: 50%;
      left: 50%;
      transform: translateX(-50%);
    }

    .placement-indicator.vertical::before { top: -4px; }
    .placement-indicator.vertical::after { bottom: -4px; }

    .section-placement-indicator {
      position: absolute;
      left: 0;
      right: 0;
      height: 3px;
      background: #3b82f6;
      border-radius: 2px;
      pointer-events: none;
      z-index: 10000;
      transition: top 80ms ease;
    }

    .section-placement-indicator::before,
    .section-placement-indicator::after {
      content: '';
      position: absolute;
      width: 8px;
      height: 8px;
      background: #3b82f6;
      border-radius: 50%;
      top: 50%;
      transform: translateY(-50%);
    }

    .section-placement-indicator::before { left: 16px; }
    .section-placement-indicator::after { right: 16px; }

    .placement-container-highlight {
      outline: 2px dashed #3b82f6 !important;
      outline-offset: -2px;
      background-color: rgba(59, 130, 246, 0.04) !important;
    }

    .placement-ghost {
      position: fixed;
      pointer-events: none;
      z-index: 10001;
      display: none;
      padding: 6px 12px 6px 8px;
      background: white;
      border: 2px solid #3b82f6;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: #1e40af;
      white-space: nowrap;
      display: none;
      align-items: center;
      gap: 6px;
    }

    .placement-ghost svg {
      flex-shrink: 0;
    }` : ''}

  </style>
</head>
<body style="margin: 0; background-color: ${pageConfig.page_background_color || '#ffffff'};">
  ${mode === 'editor' ? '<div id="hover-type-label"></div>' : ''}
  ${sectionsHTML}

  ${mode === 'editor' ? `<div id="formatting-toolbar" style="display:none; position:fixed; z-index:10002; background:#1f2937; border-radius:6px; padding:2px; gap:1px; align-items:center; box-shadow:0 4px 12px rgba(0,0,0,0.15); font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
    <button data-cmd="bold" onmousedown="event.preventDefault(); document.execCommand('bold')" style="width:28px; height:28px; display:flex; align-items:center; justify-content:center; background:transparent; border:none; border-radius:4px; cursor:pointer; color:#e5e7eb; font-weight:bold; font-size:13px;" title="Bold">B</button>
    <button data-cmd="italic" onmousedown="event.preventDefault(); document.execCommand('italic')" style="width:28px; height:28px; display:flex; align-items:center; justify-content:center; background:transparent; border:none; border-radius:4px; cursor:pointer; color:#e5e7eb; font-style:italic; font-size:13px;" title="Italic">I</button>
    <button data-cmd="underline" onmousedown="event.preventDefault(); document.execCommand('underline')" style="width:28px; height:28px; display:flex; align-items:center; justify-content:center; background:transparent; border:none; border-radius:4px; cursor:pointer; color:#e5e7eb; text-decoration:underline; font-size:13px;" title="Underline">U</button>
    <span style="width:1px; height:20px; background:#374151; margin:0 2px;"></span>
    <label style="width:28px; height:28px; display:flex; align-items:center; justify-content:center; background:transparent; border:none; border-radius:4px; cursor:pointer; position:relative;" title="Text Color">
      <span style="color:#e5e7eb; font-size:13px; font-weight:bold; border-bottom:3px solid #ef4444; line-height:1;">A</span>
      <input type="color" value="#ef4444" style="position:absolute; opacity:0; width:100%; height:100%; cursor:pointer;" onmousedown="window.__savedRange = window.getSelection().rangeCount > 0 ? window.getSelection().getRangeAt(0).cloneRange() : null;" onchange="if(window.__savedRange){var s=window.getSelection();s.removeAllRanges();s.addRange(window.__savedRange);} document.execCommand('foreColor', false, this.value); this.previousElementSibling.style.borderBottomColor = this.value; var ed=document.querySelector('[contenteditable=&quot;true&quot;]'); if(ed) ed.focus();">
    </label>
  </div>` : ''}

  ${mode === 'editor' ? `<script>
    // Click handler for selection
    document.addEventListener('click', function(e) {
      // Don't select during placement mode
      if (document.body.classList.contains('placement-mode')) {
        return;
      }
      // Don't select while editing
      if (document.querySelector('[contenteditable="true"]')) {
        return;
      }

      var section = e.target.closest('[data-section-id]');
      var element = e.target.closest('[data-element-id]');

      if (section || element) {
        e.preventDefault();
        e.stopPropagation();

        // Optimistic selection: apply .selected immediately for instant visual feedback
        document.querySelectorAll('.selected').forEach(function(el) { el.classList.remove('selected'); });
        if (element) {
          element.classList.add('selected');
        } else if (section) {
          section.classList.add('selected');
        }

        var message = {
          type: 'PREVIEW_SELECT',
          sectionId: section ? section.dataset.sectionId : null,
          elementPath: element ? element.dataset.elementId : null
        };
        window.parent.postMessage(message, '*');
      }
    });

    // HTML sanitizer for rich text content
    function sanitizeInlineHTML(html) {
      if (!html || typeof html !== 'string') return html || '';
      if (!/<[^>]+>/.test(html)) return html;

      var ALLOWED = ['br', 'span', 'strong', 'em', 'u', 'b', 'i'];
      var ALLOWED_ATTRS = ['style', 'class'];

      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');

      function cleanNode(node) {
        if (node.nodeType === 3) return node.textContent;
        if (node.nodeType !== 1) return '';

        var tag = node.tagName.toLowerCase();
        var children = '';
        for (var i = 0; i < node.childNodes.length; i++) {
          children += cleanNode(node.childNodes[i]);
        }

        if (ALLOWED.indexOf(tag) === -1) return children;

        var attrs = '';
        for (var j = 0; j < ALLOWED_ATTRS.length; j++) {
          var val = node.getAttribute(ALLOWED_ATTRS[j]);
          if (val) {
            var safe = val.replace(/javascript:/gi, '').replace(/expression\\s*\\(/gi, '').replace(/url\\s*\\(/gi, '').replace(/@import/gi, '');
            attrs += ' ' + ALLOWED_ATTRS[j] + '="' + safe.replace(/"/g, '&quot;') + '"';
          }
        }

        if (tag === 'br') return '<br>';
        if (tag === 'span' && !attrs.trim() && children === '') return '';

        return '<' + tag + attrs + '>' + children + '</' + tag + '>';
      }

      var result = '';
      for (var i = 0; i < doc.body.childNodes.length; i++) {
        result += cleanNode(doc.body.childNodes[i]);
      }
      return result;
    }

    // Double-click handler for inline text editing
    document.addEventListener('dblclick', function(e) {
      var editableElement = e.target.closest('[data-editable="true"]');
      if (!editableElement) return;

      e.stopPropagation();

      // Store original content for cancel
      editableElement.dataset.originalContent = editableElement.innerHTML;

      // Save the browser's native word selection before making editable
      var savedRange = null;
      var sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        savedRange = sel.getRangeAt(0).cloneRange();
      }

      // Make editable
      editableElement.contentEditable = 'true';
      editableElement.classList.add('editing');
      editableElement.focus();

      // Restore the word selection
      if (savedRange) {
        sel.removeAllRanges();
        sel.addRange(savedRange);
      }

      // Notify parent
      var componentId = editableElement.dataset.elementId;
      var section = editableElement.closest('[data-section-id]');
      window.parent.postMessage({
        type: 'INLINE_EDIT_START',
        sectionId: section ? section.dataset.sectionId : null,
        elementPath: componentId
      }, '*');
    });

    // Blur handler - save changes (delayed to allow toolbar interactions)
    document.addEventListener('blur', function(e) {
      var editableElement = e.target;
      if (editableElement.contentEditable !== 'true') return;

      // Delay to check if focus moved to the formatting toolbar
      var el = editableElement;
      setTimeout(function() {
        var toolbar = document.getElementById('formatting-toolbar');
        // If focus moved to the toolbar or its children (e.g. color picker), don't end editing
        if (toolbar && (toolbar.contains(document.activeElement) || document.activeElement === toolbar)) return;

        // Hide formatting toolbar
        if (toolbar) toolbar.style.display = 'none';

        // Get HTML content and sanitize it
        var rawHTML = el.innerHTML;
        var newContent = sanitizeInlineHTML(rawHTML);

        // Clean up: remove trailing <br> tags, normalize whitespace
        newContent = newContent.replace(new RegExp('(<br ?[/]?>)+$', 'i'), '');

        var componentId = el.dataset.elementId;
        var section = el.closest('[data-section-id]');

        // End editing mode
        el.contentEditable = 'false';
        el.classList.remove('editing');
        delete el.dataset.originalContent;

        // Notify parent with new content
        window.parent.postMessage({
          type: 'INLINE_EDIT_END',
          sectionId: section ? section.dataset.sectionId : null,
          elementPath: componentId,
          newContent: newContent
        }, '*');
      }, 50);
    }, true);

    // Keyboard handler for editing
    document.addEventListener('keydown', function(e) {
      var editableElement = document.querySelector('[contenteditable="true"]');
      if (!editableElement) return;

      if (e.key === 'Escape') {
        // Cancel editing - restore original content
        e.preventDefault();
        var toolbar = document.getElementById('formatting-toolbar');
        if (toolbar) toolbar.style.display = 'none';
        editableElement.innerHTML = editableElement.dataset.originalContent || '';
        editableElement.contentEditable = 'false';
        editableElement.classList.remove('editing');
        delete editableElement.dataset.originalContent;

        // Notify parent of cancel (no content change)
        window.parent.postMessage({
          type: 'INLINE_EDIT_CANCEL'
        }, '*');
      } else if (e.key === 'Enter' && !e.shiftKey) {
        // Enter inserts a line break (<br>)
        e.preventDefault();
        document.execCommand('insertLineBreak');
      } else if (e.key === 'Enter' && e.shiftKey) {
        // Shift+Enter saves and exits
        e.preventDefault();
        editableElement.blur();
      }
    });

    // Paste handler - sanitize pasted content
    document.addEventListener('paste', function(e) {
      var editableElement = document.querySelector('[contenteditable="true"]');
      if (!editableElement) return;

      e.preventDefault();
      var text = e.clipboardData.getData('text/plain');
      // Insert plain text (preserving line breaks as <br>)
      var sanitized = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\\n/g, '<br>');
      document.execCommand('insertHTML', false, sanitized);
    });

    // Selection change handler - show/hide formatting toolbar
    document.addEventListener('selectionchange', function() {
      var toolbar = document.getElementById('formatting-toolbar');
      if (!toolbar) return;

      var editableElement = document.querySelector('[contenteditable="true"]');
      if (!editableElement) {
        toolbar.style.display = 'none';
        return;
      }

      var sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        toolbar.style.display = 'none';
        return;
      }

      // Check if selection is within the editable element
      var range = sel.getRangeAt(0);
      if (!editableElement.contains(range.commonAncestorContainer)) {
        toolbar.style.display = 'none';
        return;
      }

      // Position toolbar above the selection
      var rect = range.getBoundingClientRect();
      var toolbarWidth = 180;
      var toolbarHeight = 32;
      var left = rect.left + (rect.width / 2) - (toolbarWidth / 2);
      var top = rect.top - toolbarHeight - 8;

      // Keep within viewport
      if (left < 4) left = 4;
      if (left + toolbarWidth > window.innerWidth - 4) left = window.innerWidth - toolbarWidth - 4;
      if (top < 4) top = rect.bottom + 8; // Show below if no room above

      toolbar.style.left = left + 'px';
      toolbar.style.top = top + 'px';
      toolbar.style.display = 'flex';

      // Update active states
      toolbar.querySelector('[data-cmd="bold"]').classList.toggle('active', document.queryCommandState('bold'));
      toolbar.querySelector('[data-cmd="italic"]').classList.toggle('active', document.queryCommandState('italic'));
      toolbar.querySelector('[data-cmd="underline"]').classList.toggle('active', document.queryCommandState('underline'));
    });

    // Keyboard shortcuts: duplicate (Ctrl/Cmd+D), delete with confirmation (Ctrl/Cmd+Backspace), force delete (Ctrl/Cmd+Shift+D)
    document.addEventListener('keydown', function(e) {
      var isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;

      if (e.key === 'd') {
        e.preventDefault();
        window.parent.postMessage({ type: 'DUPLICATE_SELECTED' }, '*');
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        window.parent.postMessage({ type: e.shiftKey ? 'FORCE_DELETE_SELECTED' : 'DELETE_SELECTED' }, '*');
      }
    });

    // Hover handler for visual feedback + type label
    var hoverLabel = document.getElementById('hover-type-label');

    document.addEventListener('mouseover', function(e) {
      var element = e.target.closest('[data-element-id]');
      if (element) {
        // Don't show hover during placement mode
        if (document.body.classList.contains('placement-mode')) {
          return;
        }

        // Remove hover from all other elements
        document.querySelectorAll('[data-element-id].hover').forEach(function(el) {
          el.classList.remove('hover');
        });
        element.classList.add('hover');

        // Show type label only if element is NOT selected
        if (!element.classList.contains('selected') && hoverLabel) {
          var componentType = element.getAttribute('data-component-type');
          if (componentType) {
            hoverLabel.textContent = componentType.charAt(0).toUpperCase() + componentType.slice(1);
            hoverLabel.style.display = 'block';
            // Position at top-left of element
            var rect = element.getBoundingClientRect();
            var labelTop = rect.top - 22;
            // If near top of viewport, show inside element instead
            if (labelTop < 2) {
              labelTop = rect.top + 2;
            }
            hoverLabel.style.top = labelTop + 'px';
            hoverLabel.style.left = rect.left + 'px';
          }
        }
      }
    });

    document.addEventListener('mouseout', function(e) {
      var element = e.target.closest('[data-element-id]');
      if (element) {
        element.classList.remove('hover');
        if (hoverLabel) {
          hoverLabel.style.display = 'none';
        }
      }
    });

    // Pinch-to-zoom handler - forward to parent to prevent browser zoom
    document.addEventListener('wheel', function(e) {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        window.parent.postMessage({
          type: 'PREVIEW_ZOOM',
          deltaY: e.deltaY
        }, '*');
      }
    }, { passive: false });

    // Listen for selection updates from parent
    window.addEventListener('message', function(e) {
      if (e.data && e.data.type === 'UPDATE_SELECTION') {
        // Remove all selected classes
        document.querySelectorAll('.selected').forEach(function(el) {
          el.classList.remove('selected');
        });

        // Add selected class to the selected element
        if (e.data.sectionId) {
          var section = document.querySelector('[data-section-id="' + e.data.sectionId + '"]');
          if (section) {
            section.classList.add('selected');
          }
        }

        if (e.data.elementPath) {
          var element = document.querySelector('[data-element-id="' + e.data.elementPath + '"]');
          if (element) {
            element.classList.add('selected');
          }
        }
      }

      // Placement mode messages
      if (e.data && e.data.type === 'ENTER_PLACEMENT_MODE') {
        enterPlacementMode(e.data.componentType, e.data.placementLevel);
      }
      if (e.data && e.data.type === 'EXIT_PLACEMENT_MODE') {
        exitPlacementMode();
      }
    });

    // ================================================================
    // Placement Mode System
    // ================================================================
    var placementActive = false;
    var placementLevel = 'component'; // 'component' or 'section'
    var placementIndicator = null;
    var sectionIndicator = null;
    var highlightedContainer = null;
    var cursorGhost = null;
    var spreadChildren = []; // currently shifted children
    var spreadContainerEl = null;
    var spreadIndex = -1;

    var COMPONENT_ICONS = {
      row: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="6" width="22" height="12" rx="2"/><line x1="9" y1="6" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="18"/></svg>',
      column: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>',
      container: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>',
      block: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>',
      text: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9.5" y1="20" x2="14.5" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>',
      image: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
      button: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="6"/></svg>',
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
      spacer: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="18" x2="20" y2="18"/><line x1="12" y1="10" x2="12" y2="14"/><polyline points="9 11 12 8 15 11"/><polyline points="9 13 12 16 15 13"/></svg>',
      video: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
      accordion: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="6" rx="1"/><rect x="3" y="13" width="18" height="6" rx="1" opacity="0.5"/></svg>',
      link: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
      listItem: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1" fill="currentColor"/><circle cx="4" cy="12" r="1" fill="currentColor"/><circle cx="4" cy="18" r="1" fill="currentColor"/></svg>',
      section: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/></svg>'
    };

    var COMPONENT_LABELS = {
      row: 'Row', column: 'Column', container: 'Container', block: 'Block', text: 'Text',
      image: 'Image', button: 'Button', icon: 'Icon', spacer: 'Spacer',
      video: 'Video', accordion: 'Accordion', link: 'Link', listItem: 'List Item',
      section: 'Section'
    };

    function createCursorGhost(componentType) {
      if (cursorGhost) cursorGhost.remove();
      var el = document.createElement('div');
      el.className = 'placement-ghost';
      var icon = COMPONENT_ICONS[componentType] || '';
      var label = COMPONENT_LABELS[componentType] || componentType;
      el.innerHTML = icon + '<span>' + label + '</span>';
      document.body.appendChild(el);
      cursorGhost = el;
      return el;
    }

    function createIndicator() {
      if (placementIndicator) return placementIndicator;
      var el = document.createElement('div');
      el.className = 'placement-indicator horizontal';
      el.style.display = 'none';
      document.body.appendChild(el);
      placementIndicator = el;
      return el;
    }

    function findDropZone(clientX, clientY) {
      var elements = document.elementsFromPoint(clientX, clientY);
      var container = null;

      for (var i = 0; i < elements.length; i++) {
        if (elements[i].hasAttribute('data-can-drop')) {
          container = elements[i];
          break;
        }
      }

      if (!container) return null;

      var sectionEl = container.closest('[data-section-id]');
      if (!sectionEl) return null;
      var sectionId = sectionEl.dataset.sectionId;

      var flexDir = container.getAttribute('data-flex-direction') || 'column';
      var isHorizontal = (flexDir === 'row' || flexDir === 'row-reverse');

      // parentPath: if container has data-element-id, use it; otherwise it's the section root
      var parentPath = container.hasAttribute('data-element-id') ? container.dataset.elementId : 'root';

      // Get direct children with data-element-id
      var children = [];
      for (var j = 0; j < container.children.length; j++) {
        var child = container.children[j];
        if (child.hasAttribute('data-element-id') || child.hasAttribute('data-section-id')) {
          children.push(child);
        }
      }

      var containerRect = container.getBoundingClientRect();

      // Empty container
      if (children.length === 0) {
        return {
          sectionId: sectionId,
          parentPath: parentPath,
          index: 0,
          edgePos: isHorizontal ? containerRect.left + containerRect.width / 2 : containerRect.top + containerRect.height / 2,
          isHorizontal: isHorizontal,
          containerEl: container,
          isEmpty: true,
          children: children
        };
      }

      // Find nearest edge between children
      var bestIndex = 0;
      var bestDist = Infinity;
      var bestEdgePos = 0;

      for (var k = 0; k <= children.length; k++) {
        var edgePos;
        if (k === 0) {
          var firstRect = children[0].getBoundingClientRect();
          edgePos = isHorizontal ? firstRect.left : firstRect.top;
        } else if (k === children.length) {
          var lastRect = children[children.length - 1].getBoundingClientRect();
          edgePos = isHorizontal ? lastRect.right : lastRect.bottom;
        } else {
          var prevRect = children[k - 1].getBoundingClientRect();
          var nextRect = children[k].getBoundingClientRect();
          edgePos = isHorizontal
            ? (prevRect.right + nextRect.left) / 2
            : (prevRect.bottom + nextRect.top) / 2;
        }

        var cursorPos = isHorizontal ? clientX : clientY;
        var dist = Math.abs(cursorPos - edgePos);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = k;
          bestEdgePos = edgePos;
        }
      }

      return {
        sectionId: sectionId,
        parentPath: parentPath,
        index: bestIndex,
        edgePos: bestEdgePos,
        isHorizontal: isHorizontal,
        containerEl: container,
        isEmpty: false,
        children: children
      };
    }

    var SPREAD_PX = 14;

    function clearSpread() {
      for (var i = 0; i < spreadChildren.length; i++) {
        spreadChildren[i].style.transform = '';
      }
      spreadChildren = [];
      spreadContainerEl = null;
      spreadIndex = -1;
    }

    function applySpread(zone, children) {
      // Skip if same zone
      if (zone.containerEl === spreadContainerEl && zone.index === spreadIndex) return;
      clearSpread();
      if (zone.isEmpty || children.length === 0) return;

      spreadContainerEl = zone.containerEl;
      spreadIndex = zone.index;

      var axis = zone.isHorizontal ? 'X' : 'Y';
      for (var i = 0; i < children.length; i++) {
        var shift = i < zone.index ? -SPREAD_PX : SPREAD_PX;
        children[i].style.transform = 'translate' + axis + '(' + shift + 'px)';
        spreadChildren.push(children[i]);
      }
    }

    function createSectionIndicator() {
      if (sectionIndicator) return sectionIndicator;
      var el = document.createElement('div');
      el.className = 'section-placement-indicator';
      el.style.display = 'none';
      document.body.appendChild(el);
      sectionIndicator = el;
      return el;
    }

    function findSectionDropZone(clientX, clientY) {
      var sectionEls = document.querySelectorAll('body > [data-section-id]');
      var sections = Array.prototype.slice.call(sectionEls);

      // Empty page — place at top
      if (sections.length === 0) {
        return { index: 0, edgePos: 0, isEmpty: true };
      }

      var bestIndex = 0;
      var bestDist = Infinity;
      var bestEdgePos = 0;

      for (var k = 0; k <= sections.length; k++) {
        var edgePos;
        if (k === 0) {
          edgePos = sections[0].getBoundingClientRect().top;
        } else if (k === sections.length) {
          edgePos = sections[sections.length - 1].getBoundingClientRect().bottom;
        } else {
          var prevRect = sections[k - 1].getBoundingClientRect();
          var nextRect = sections[k].getBoundingClientRect();
          edgePos = (prevRect.bottom + nextRect.top) / 2;
        }

        var dist = Math.abs(clientY - edgePos);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = k;
          bestEdgePos = edgePos;
        }
      }

      return { index: bestIndex, edgePos: bestEdgePos, isEmpty: false };
    }

    function onSectionPlacementMouseMove(e) {
      if (!placementActive) return;
      var indicator = createSectionIndicator();

      // Position cursor ghost
      if (cursorGhost) {
        cursorGhost.style.display = 'flex';
        cursorGhost.style.left = (e.clientX + 16) + 'px';
        cursorGhost.style.top = (e.clientY + 16) + 'px';
      }

      var zone = findSectionDropZone(e.clientX, e.clientY);
      if (!zone) {
        indicator.style.display = 'none';
        return;
      }

      indicator.style.display = 'block';
      indicator.style.top = (zone.edgePos - 1.5 + window.scrollY) + 'px';
    }

    function onSectionPlacementClick(e) {
      if (!placementActive) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      var zone = findSectionDropZone(e.clientX, e.clientY);
      if (!zone) return;

      window.parent.postMessage({
        type: 'SECTION_PLACEMENT_CLICK',
        index: zone.index
      }, '*');
    }

    function onComponentPlacementMouseMove(e) {
      var zone = findDropZone(e.clientX, e.clientY);
      var indicator = createIndicator();

      // Clear previous container highlight
      if (highlightedContainer) {
        highlightedContainer.classList.remove('placement-container-highlight');
        highlightedContainer = null;
      }

      if (!zone) {
        indicator.style.display = 'none';
        clearSpread();
        return;
      }

      // Highlight the target container
      zone.containerEl.classList.add('placement-container-highlight');
      highlightedContainer = zone.containerEl;

      // Apply spread to children
      applySpread(zone, zone.children);

      if (zone.isEmpty) {
        // Show indicator centered in empty container
        var cr = zone.containerEl.getBoundingClientRect();
        if (zone.isHorizontal) {
          indicator.className = 'placement-indicator vertical';
          indicator.style.display = 'block';
          indicator.style.left = (cr.left + cr.width / 2 - 1.5) + 'px';
          indicator.style.top = (cr.top + 4) + 'px';
          indicator.style.height = (cr.height - 8) + 'px';
          indicator.style.width = '3px';
        } else {
          indicator.className = 'placement-indicator horizontal';
          indicator.style.display = 'block';
          indicator.style.left = (cr.left + 4) + 'px';
          indicator.style.top = (cr.top + cr.height / 2 - 1.5) + 'px';
          indicator.style.width = (cr.width - 8) + 'px';
          indicator.style.height = '3px';
        }
        return;
      }

      var cRect = zone.containerEl.getBoundingClientRect();
      if (zone.isHorizontal) {
        indicator.className = 'placement-indicator vertical';
        indicator.style.display = 'block';
        indicator.style.left = (zone.edgePos - 1.5) + 'px';
        indicator.style.top = (cRect.top + 4) + 'px';
        indicator.style.height = (cRect.height - 8) + 'px';
        indicator.style.width = '3px';
      } else {
        indicator.className = 'placement-indicator horizontal';
        indicator.style.display = 'block';
        indicator.style.left = (cRect.left + 4) + 'px';
        indicator.style.top = (zone.edgePos - 1.5) + 'px';
        indicator.style.width = (cRect.width - 8) + 'px';
        indicator.style.height = '3px';
      }
    }

    function onPlacementMouseMove(e) {
      if (!placementActive) return;

      // Position cursor ghost
      if (cursorGhost) {
        cursorGhost.style.display = 'flex';
        cursorGhost.style.left = (e.clientX + 16) + 'px';
        cursorGhost.style.top = (e.clientY + 16) + 'px';
      }

      if (placementLevel === 'section') {
        onSectionPlacementMouseMove(e);
      } else {
        onComponentPlacementMouseMove(e);
      }
    }

    function onPlacementClick(e) {
      if (!placementActive) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      if (placementLevel === 'section') {
        onSectionPlacementClick(e);
      } else {
        var zone = findDropZone(e.clientX, e.clientY);
        if (!zone) return;

        window.parent.postMessage({
          type: 'PLACEMENT_CLICK',
          sectionId: zone.sectionId,
          parentPath: zone.parentPath,
          index: zone.index
        }, '*');
      }
    }

    function onPlacementKeydown(e) {
      if (!placementActive) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        window.parent.postMessage({ type: 'PLACEMENT_CANCEL' }, '*');
      }
    }

    function enterPlacementMode(componentType, level) {
      if (placementActive) return;
      placementActive = true;
      placementLevel = level || 'component';
      document.body.classList.add('placement-mode');
      createCursorGhost(componentType);
      // Remove selection outlines
      document.querySelectorAll('.selected').forEach(function(el) { el.classList.remove('selected'); });
      document.querySelectorAll('.hover').forEach(function(el) { el.classList.remove('hover'); });
      document.addEventListener('mousemove', onPlacementMouseMove);
      document.addEventListener('click', onPlacementClick, true);
      document.addEventListener('keydown', onPlacementKeydown);
    }

    function exitPlacementMode() {
      if (!placementActive) return;
      placementActive = false;
      placementLevel = 'component';
      document.body.classList.remove('placement-mode');
      clearSpread();
      if (placementIndicator) {
        placementIndicator.style.display = 'none';
      }
      if (sectionIndicator) {
        sectionIndicator.style.display = 'none';
      }
      if (cursorGhost) {
        cursorGhost.remove();
        cursorGhost = null;
      }
      if (highlightedContainer) {
        highlightedContainer.classList.remove('placement-container-highlight');
        highlightedContainer = null;
      }
      document.removeEventListener('mousemove', onPlacementMouseMove);
      document.removeEventListener('click', onPlacementClick, true);
      document.removeEventListener('keydown', onPlacementKeydown);
    }
  </script>` : ''}
</body>
</html>`;

  // Reset module-level state
  _currentMode = 'editor';
  _publishConfig = {};

  return html;
}

/**
 * Convenience wrapper for generating publish-ready HTML
 * @param {Array} sections - Page sections array
 * @param {Object} pageConfig - Page configuration
 * @param {Object} publishConfig - { title, metaDescription, faviconUrl, siteSections, pageIdToSlugMap }
 * @returns {string} Complete standalone HTML document string
 */
export function generatePublishHTML(sections, pageConfig = {}, publishConfig = {}) {
  return generatePreviewHTML(sections, pageConfig, 'publish', publishConfig);
}

export default generatePreviewHTML;
